import { useState, VFC } from "react";

import { useUpdateShipmentMutation } from "api/rest/shipments/shipmentsApi";
import { useNotificationWatcher } from "hooks/notifications/useNotificationWatcher";
import useLDFlag from "hooks/useLDFlag";
import { useEffectOnce } from "usehooks-ts";

import FilesControl, {
  OnUploadSuccessCallback,
  useFilesContext,
} from "../../../../components/file-uploads/FilesControl";
import { shipmentDocumentsActions, shipmentDocumentSelectors } from "../store/shipmentDocumentsSlice";
import { useShipmentsSliceSelector } from "../store/shipmentsStore";
import { Shipment } from "../types/domain";
import ConfirmDeleteShipmentFileView from "./ConfirmDeleteShipmentFileView";
import DocumentsBulkActionsContainer from "./documents/containers/DocumentsBulkActionsContainer";
import ShipmentDocumentsTabView from "./ShipmentDocumentsTabView";

interface ShipmentDocumentsTabContainerProps {
  shipment: Shipment;
}

const ShipmentDocumentsTabInnerContainer: VFC<{ shipment: Shipment }> = (props) => {
  const { shipment } = props;
  const { useGetFiles, onDeleteFileFromDB } = useFilesContext();
  const files = useGetFiles();
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [fileDeleteRequestedId, setFileDeleteRequestedId] = useState<string | null>(null);
  const enableShareDocumentsViaChat = useLDFlag("enableShareDocuments");
  const enableShareDocumentsViaEmail = useLDFlag("enableShareDocumentsViaEmail");
  const shipmentDocumentsState = useShipmentsSliceSelector((state) => state.shipmentDocumentsSlice);
  const { selectAllState } = useShipmentsSliceSelector((state) => state.shipmentDocumentsSlice);
  const toggleFile = shipmentDocumentsActions.useToggleFile();
  const toggleAllFiles = shipmentDocumentsActions.useToggleAllFiles();
  const unselectAllFiles = shipmentDocumentsActions.useUnselectAllFiles();

  const handleDeleteRequest = (id: string) => {
    setFileDeleteRequestedId(id);
    setDeleteConfirmationOpen(true);
  };
  const handleCloseCancelDialog = () => {
    setDeleteConfirmationOpen(false);
    setFileDeleteRequestedId(null);
  };

  const handleDeleteFile = async (id: string) => {
    await onDeleteFileFromDB(id);
    handleCloseCancelDialog();
  };

  useEffectOnce(() => {
    unselectAllFiles();
  });

  return (
    <>
      <ConfirmDeleteShipmentFileView
        fileId={fileDeleteRequestedId}
        isOpen={deleteConfirmationOpen}
        handleClose={handleCloseCancelDialog}
        handleDelete={handleDeleteFile}
      />
      <DocumentsBulkActionsContainer shipment={shipment} />
      <ShipmentDocumentsTabView
        isFetching={!files}
        documents={files}
        handleDeleteRequest={handleDeleteRequest}
        enableShareDocuments={enableShareDocumentsViaChat || enableShareDocumentsViaEmail}
        headerCheckboxState={selectAllState}
        onClickHeaderCheckbox={() => toggleAllFiles(files)}
        onClickRowCheckbox={(file) => toggleFile(file)}
        isChecked={(file) => !!shipmentDocumentSelectors.selectById(shipmentDocumentsState, file.localId)}
      />
    </>
  );
};

const ShipmentDocumentsTabContainer: VFC<ShipmentDocumentsTabContainerProps> = ({ shipment }) => {
  const [fileIds, setFileIds] = useState<string[]>(shipment.fileIds);
  const [updateShipment] = useUpdateShipmentMutation();
  useNotificationWatcher([`bolGenerationComplete:${shipment.id}`]);

  const handleUploadFileSuccess: OnUploadSuccessCallback = async ({ fileId }) => {
    const newFileIds = [...fileIds, fileId];

    setFileIds(newFileIds);
    updateShipment({
      urlParams: { shipmentId: shipment.id },
      body: { fileIds: newFileIds, versionNumber: shipment.versionNumber },
    });
  };

  const handleDeleteFileSuccess = async (fileId: string) => {
    setFileIds((prev) => prev.filter((value) => value !== fileId));
  };

  return (
    <FilesControl
      fileIds={fileIds}
      onUploadSuccess={handleUploadFileSuccess}
      onDeleteSuccess={handleDeleteFileSuccess}
      dropzoneOptions={{ maxFiles: 4, multiple: false, maxSize: 5e6 }}
    >
      <ShipmentDocumentsTabInnerContainer shipment={shipment} />
    </FilesControl>
  );
};

export default ShipmentDocumentsTabContainer;
